import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "list"]
  static values = {
    kind: String,
    url: String,
    makerInputId: String
  }

  connect() {
    this.activeIndex = -1
    this.isComposing = false
    this.requestId = 0
    this.lastRequestKey = null
    this.suggestions = []
    this.hide()
  }

  disconnect() {
    window.clearTimeout(this.blurTimer)
    window.clearTimeout(this.updateTimer)
  }

  input(event) {
    // Safari can dispatch an input event while Japanese text is still being
    // composed, before the input element exposes the composed value. The
    // composition handlers below use event.data instead, so avoid replacing
    // that pending query with an empty/stale input value here.
    if (this.isComposing || event.isComposing) return

    this.scheduleUpdate()
  }

  focus() {
    this.scheduleUpdate()
  }

  keydown(event) {
    if (this.isComposing || event.isComposing || this.listTarget.classList.contains("hidden")) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.move(1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.move(-1)
    } else if (event.key === "Enter" && this.activeIndex >= 0) {
      event.preventDefault()
      this.select(this.suggestions[this.activeIndex])
    } else if (event.key === "Escape") {
      this.hide()
    }
  }

  beforeInput(event) {
    if (!event.isComposing && !this.isComposing) return

    this.scheduleUpdate(this.composedValue(event.data))
  }

  compositionStart(event) {
    this.isComposing = true
    this.compositionValueBeforeStart = this.inputTarget.value
    this.compositionStart = this.inputTarget.selectionStart ?? this.inputTarget.value.length
    this.compositionEnd = this.inputTarget.selectionEnd ?? this.compositionStart
  }

  compositionUpdate(event) {
    this.scheduleUpdate(this.composedValue(event.data))
  }

  compositionEnd(event) {
    const composedValue = this.composedValue(event.data)

    this.isComposing = false
    this.clearCompositionState()
    this.scheduleUpdate(this.inputTarget.value || composedValue)
  }

  blur() {
    window.clearTimeout(this.blurTimer)
    this.blurTimer = window.setTimeout(() => this.hide(), 120)
  }

  scheduleUpdate(value = this.inputTarget.value) {
    // iPhone Safari can keep input.value unchanged until the user commits a
    // Japanese conversion. Capture the event value now so "と" can request
    // "トヨタ" before the conversion is confirmed.
    const query = String(value)

    window.clearTimeout(this.updateTimer)
    this.updateTimer = window.setTimeout(() => this.update(query), 60)
  }

  async update(value = this.inputTarget.value) {
    const query = String(value).trim()
    if (query.length < 1 || this.requiresMaker && this.makerValue.length < 1) {
      this.lastRequestKey = null
      this.hide()
      return
    }

    const requestKey = [this.kindValue, query, this.requiresMaker ? this.makerValue : ""].join("\u0000")
    if (requestKey === this.lastRequestKey) {
      if (this.suggestions.length > 0) this.render()
      return
    }

    this.lastRequestKey = requestKey
    const requestId = ++this.requestId
    const params = new URLSearchParams({ field: this.kindValue, q: query })

    if (this.requiresMaker) params.set("maker", this.makerValue)

    try {
      const response = await fetch(`${this.urlValue}?${params.toString()}`, {
        headers: { Accept: "application/json" }
      })

      if (!response.ok) throw new Error(`Suggestion request failed: ${response.status}`)

      const payload = await response.json()
      if (requestId !== this.requestId) return

      this.suggestions = Array.isArray(payload.suggestions) ? payload.suggestions : []
      this.suggestions.length > 0 ? this.render() : this.hide()
    } catch (error) {
      if (requestId === this.requestId) {
        this.lastRequestKey = null
        this.hide()
      }
    }
  }

  move(direction) {
    this.activeIndex = (this.activeIndex + direction + this.suggestions.length) % this.suggestions.length

    this.listTarget.querySelectorAll("[role='option']").forEach((option, index) => {
      const selected = index === this.activeIndex
      option.classList.toggle("vehicle-suggest__option--active", selected)
      option.setAttribute("aria-selected", String(selected))
      if (selected) this.inputTarget.setAttribute("aria-activedescendant", option.id)
    })
  }

  select(option) {
    if (!option) return

    this.inputTarget.value = option.label
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.hide()
    this.inputTarget.focus()
  }

  render() {
    const fragment = document.createDocumentFragment()

    this.suggestions.forEach((suggestion, index) => {
      const option = document.createElement("button")
      option.type = "button"
      option.id = `${this.inputTarget.id}--suggestion-${index}`
      option.className = "vehicle-suggest__option"
      option.setAttribute("role", "option")
      option.setAttribute("aria-selected", "false")
      option.textContent = suggestion.label
      option.addEventListener("pointerdown", (event) => {
        event.preventDefault()
        this.select(suggestion)
      })
      fragment.appendChild(option)
    })

    // `replaceChildren` is not available in older iPhone Safari versions.
    // Use the long-supported DOM APIs so the fetched suggestions are rendered
    // consistently on the customer estimate form.
    this.listTarget.textContent = ""
    this.listTarget.appendChild(fragment)
    this.listTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  hide() {
    if (!this.hasListTarget || !this.hasInputTarget) return

    window.clearTimeout(this.updateTimer)
    this.listTarget.classList.add("hidden")
    this.listTarget.textContent = ""
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
    this.activeIndex = -1
  }

  get requiresMaker() {
    return this.kindValue === "model"
  }

  get makerValue() {
    return document.getElementById(this.makerInputIdValue)?.value.trim() || ""
  }

  composedValue(data) {
    const value = this.compositionValueBeforeStart ?? this.inputTarget.value
    const start = this.compositionStart ?? this.inputTarget.selectionStart ?? value.length
    const end = this.compositionEnd ?? this.inputTarget.selectionEnd ?? start

    return `${value.slice(0, start)}${data || ""}${value.slice(end)}`
  }

  clearCompositionState() {
    this.compositionValueBeforeStart = null
    this.compositionStart = null
    this.compositionEnd = null
  }
}
